import { useRef, type ComponentProps, type FocusEvent, type ReactNode } from 'react';
import { Menu as Base } from '@base-ui/react/menu';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { returnFocusUnlessMoved } from './focus';
import { Kbd } from './Kbd';

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

/**
 * The popup scrolls inside the room Base UI leaves it (`--available-height` is set on the positioner): a long menu in a
 * short window (the board menu at 768 px) never runs off the screen, and its scroll never reaches the page behind.
 */
export const menuPopupClass =
  'os-pop max-h-(--available-height) min-w-44 overflow-y-auto overscroll-contain rounded-2xl bg-chrome p-1.5 font-sans text-sm text-fg shadow-float outline-none';
/** A menu row: the geometry, highlight, icon/shortcut slots and destructive colour live in `.os-menu-item` (globals.css). */
export const menuItemClass = 'os-menu-item pointer-coarse:min-h-11';

const firstItem = (popup: HTMLElement) =>
  popup.querySelector<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])');

/** Menu popup in a portal, positioned against its trigger; marked as canvas UI so the editor leaves its events alone. */
function Popup({
  label,
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  className,
  finalFocus,
  anchor,
  focusFirst = false,
}: {
  label?: string;
  children: ReactNode;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  className?: string;
  /** Where the focus goes on close (Base UI); false leaves it to what an item opened. */
  finalFocus?: ComponentProps<typeof Base.Popup>['finalFocus'];
  /** Positions it against this instead of its trigger (a point on the canvas: `pointAnchor`). */
  anchor?: ComponentProps<typeof Base.Positioner>['anchor'];
  /** Moves the focus to the first enabled item on open (a menu opened at a point, without a trigger). */
  focusFirst?: boolean;
}) {
  const popup = useRef<HTMLDivElement>(null);
  // A given function still only runs while the focus is ours: an item that focused the canvas keeps it there.
  const moved = returnFocusUnlessMoved(popup);
  const final: typeof finalFocus =
    typeof finalFocus === 'function'
      ? (type) => (moved() ? finalFocus(type) : false)
      : (finalFocus ?? moved);
  return (
    <Base.Portal>
      <Base.Positioner
        side={side}
        align={align}
        sideOffset={sideOffset}
        anchor={anchor}
        className="z-(--layer-popover)"
        {...{ [CANVAS_UI]: '' }}
      >
        <Base.Popup
          ref={popup}
          aria-label={label}
          finalFocus={final}
          // Base UI focuses the popup itself on open: pass it on to the first enabled item.
          onFocus={
            focusFirst
              ? (e: FocusEvent<HTMLElement>) =>
                  e.target === e.currentTarget && firstItem(e.currentTarget)?.focus()
              : undefined
          }
          className={cn(menuPopupClass, className)}
        >
          {children}
        </Base.Popup>
      </Base.Positioner>
    </Base.Portal>
  );
}

type RowProps = {
  /** A Lucide icon (any node) in the 20 px leading slot; give every row of a menu one, or none. */
  icon?: ReactNode;
  /** The key combination shown at the end of the row (hidden under a coarse pointer, which has no keyboard). */
  shortcut?: string;
  /** Marks a destructive row (accent text). The separator before the first one is the caller's. */
  destructive?: boolean;
};

/** The row's content; without an icon or a shortcut it is the children as they are, so older call sites do not change. */
function Row({
  icon,
  shortcut,
  children,
}: Pick<RowProps, 'icon' | 'shortcut'> & { children?: ReactNode }) {
  if (!icon && !shortcut) return children;
  return (
    <>
      {icon && (
        <span data-icon="" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
      {shortcut && (
        <span data-shortcut="">
          <Kbd>{shortcut}</Kbd>
        </span>
      )}
    </>
  );
}

function Item({
  className,
  icon,
  shortcut,
  destructive,
  children,
  ...rest
}: ComponentProps<typeof Base.Item> & RowProps) {
  return (
    <Base.Item
      className={cn(menuItemClass, className as string)}
      data-destructive={destructive ? '' : undefined}
      {...rest}
    >
      <Row icon={icon} shortcut={shortcut}>
        {children}
      </Row>
    </Base.Item>
  );
}

function SubmenuTrigger({
  className,
  icon,
  children,
  ...rest
}: ComponentProps<typeof Base.SubmenuTrigger> & Pick<RowProps, 'icon'>) {
  return (
    <Base.SubmenuTrigger
      className={cn(menuItemClass, 'justify-between', className as string)}
      {...rest}
    >
      <Row icon={icon}>{children}</Row>
    </Base.SubmenuTrigger>
  );
}

/** A sub-menu's popup: opens sideways from its trigger, with the same pop as the menu it comes out of. */
function SubmenuPopup(props: ComponentProps<typeof Popup>) {
  return <Popup side="right" align="start" sideOffset={4} {...props} />;
}

function Separator() {
  return <div role="separator" className="-mx-1 my-1 h-px bg-border" />;
}

/** A named run of rows (`role="group"`, named by its `Menu.Label`). */
function Group({ className, ...rest }: ComponentProps<typeof Base.Group>) {
  return <Base.Group className={cn('flex flex-col', className as string)} {...rest} />;
}

/** The small-caps heading of a `Menu.Group`; its colour moves to the readable subtle text token when that lands. */
function Label({ className, ...rest }: ComponentProps<typeof Base.GroupLabel>) {
  return (
    <Base.GroupLabel
      className={cn(
        'cursor-default select-none px-2.5 pb-1 pt-2 font-sans text-xs font-medium tracking-wide text-[var(--color-fg-subtle-text,var(--color-fg-subtle))] [font-variant-caps:all-small-caps]',
        className as string,
      )}
      {...rest}
    />
  );
}

/** A zero-size anchor at client coordinates, for menus opened at a point (context menu, quick create). */
export function pointAnchor(x: number, y: number) {
  return { getBoundingClientRect: () => DOMRect.fromRect({ x, y, width: 0, height: 0 }) };
}

export const Menu = {
  Root: Base.Root,
  Trigger: Base.Trigger,
  Popup,
  Item,
  Separator,
  Group,
  Label,
  SubmenuRoot: Base.SubmenuRoot,
  SubmenuTrigger,
  SubmenuPopup,
};
